import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Flex, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import { useAppDispatch } from '@/store/hooks';
import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice';
import { useCancelAllScheduledAppointmentsMutation } from '@/store/slices/api/schedules/schedulesApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import styles from '../DeviceScheduleModal.module.scss';
import { EditScheduleStepProps } from '../types';

const CancelActiveSchedules: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  setLoading,
  deviceInfo,
  patientSFId
}) => {
  const { id: patientId } = useParams() as { id: string };
  const [showConfirmation, setShowConfirmation] = useState(false);
  const dispatch = useAppDispatch();
  const [cancelAllAppointments] = useCancelAllScheduledAppointmentsMutation();

  const handleCancelAll = async () => {
    if (!patientSFId) {
      notifications.show({
        title: 'Error',
        message: 'Patient information is missing',
        color: 'red',
        icon: <IconX size="1.1rem" />
      });
      return;
    }

    setLoading(true);

    try {
      const cancelData = {
        patientSFId: patientSFId,
        modelNumber: deviceInfo?.modelNumber || '',
        serialNumber: deviceInfo?.serialNumber || '',
        deviceId: deviceInfo?.deviceId || ''
      };

      const result = await cancelAllAppointments(cancelData).unwrap();

      notifications.show({
        title: 'Success',
        message: result.message || 'All active schedules have been cancelled successfully',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      dispatch(patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patientId }]));

      setShowConfirmation(true);
    } catch (error) {
      console.error('Error cancelling appointments:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to cancel schedules. Please try again.',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  if (showConfirmation) {
    return (
      <Stack gap={'xs'} px={20} pt={16}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          Schedules Cancelled
        </Title>
        <Text size="sm" mb={8}>
          All active schedules have been successfully cancelled.
        </Text>
        <Group className={styles.buttonGroup} justify="flex-end">
          <Button color="plum.9" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          Cancel All Active Schedules
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          This will cancel all active schedules for this patient. This action cannot be undone.
        </Text>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleCancelAll}
          className={styles.modalButton}
          disabled={loading || !patientSFId}
          loading={loading}>
          Cancel All
        </Button>
      </Group>
    </Stack>
  );
};

export default CancelActiveSchedules;
